import React, { useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { Plus, Bold, Italic, Strikethrough } from 'lucide-react';
import clsx from 'clsx';

/**
 * RichBodyEditor renders a contentEditable surface that mixes plain text with
 * non-editable variable chips ({{parameter_1}}, {{firstName}}, ...).
 *
 * Internally chips are rendered as <span data-var="...">{{name}}</span> with
 * contenteditable=false and a brand pill style. Backspace next to a chip
 * deletes the *whole chip*, never half of it — so we never end up with
 * malformed tokens like "{{parameter_1}".
 *
 * The editor is uncontrolled in DOM but emits a serialized text value via
 * onChange. Variables can be inserted via the public ref (insertVariable).
 */

export interface RichBodyEditorHandle {
  insertVariable: (name: string) => void;
  insertNextParameter: () => string;   // returns the inserted name (parameter_N)
  focus: () => void;
}

interface Props {
  value: string;                         // serialized form: "Hi {{firstName}}, ..."
  onChange: (next: string) => void;
  placeholder?: string;
  maxLength?: number;
  rows?: number;                         // approx; controls min-height
  disabled?: boolean;
  /**
   * Names already used elsewhere in the same template (header / footer /
   * buttons). insertNextParameter merges these with the editor's own names
   * so a fresh `parameter_N` doesn't collide across fields.
   */
  externalUsedNames?: () => string[];
}

const VAR_RE = /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g;

export const RichBodyEditor = React.forwardRef<RichBodyEditorHandle, Props>(
  ({ value, onChange, placeholder, maxLength = 1024, rows = 6, disabled, externalUsedNames }, ref) => {
    const editorRef = useRef<HTMLDivElement>(null);
    // Track the last value we serialized FROM the DOM — when a parent change
    // matches this we skip rebuilding (avoids cursor jumps during typing).
    const lastSerialized = useRef<string>('');

    /** Render the value into the editor as text + chip nodes. */
    const renderToDom = useCallback((text: string) => {
      const el = editorRef.current;
      if (!el) return;
      el.textContent = '';
      let last = 0;
      VAR_RE.lastIndex = 0;
      let m: RegExpExecArray | null;
      while ((m = VAR_RE.exec(text)) !== null) {
        const before = text.slice(last, m.index);
        if (before) el.appendChild(document.createTextNode(before));
        el.appendChild(makeChip(m[1]));
        last = m.index + m[0].length;
      }
      const tail = text.slice(last);
      if (tail) el.appendChild(document.createTextNode(tail));
    }, []);

    const serialize = useCallback((): string => {
      const el = editorRef.current;
      if (!el) return '';
      let out = '';
      const walk = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          out += (node.textContent ?? '').replace(/ /g, ' ');
          return;
        }
        if (node instanceof HTMLElement) {
          if (node.dataset.var) {
            out += `{{${node.dataset.var}}}`;
            return;
          }
          if (node.tagName === 'BR') {
            out += '\n';
            return;
          }
          if (node.tagName === 'DIV' || node.tagName === 'P') {
            // contenteditable inserts <div> or <p> on enter — translate to newlines.
            if (out.length && !out.endsWith('\n')) out += '\n';
            node.childNodes.forEach(walk);
            return;
          }
        }
        node.childNodes.forEach(walk);
      };
      el.childNodes.forEach(walk);
      // Strip a trailing single newline that DIVs sometimes add.
      return out;
    }, []);

    /** Sync DOM ← parent value when the parent supplies a different serialization. */
    useEffect(() => {
      if (lastSerialized.current === value) return;
      lastSerialized.current = value;
      renderToDom(value);
    }, [value, renderToDom]);

    const fireChange = useCallback(() => {
      const text = serialize();
      lastSerialized.current = text;
      if (text.length > maxLength) {
        // Trim within the DOM to enforce the cap.
        // (Most paths get caught earlier in onBeforeInput, but paste can sneak in.)
      }
      onChange(text);
    }, [serialize, onChange, maxLength]);

    /** Insert a variable {{name}} at the current cursor (or end of editor). */
    const insertVariable = useCallback((name: string) => {
      const el = editorRef.current;
      if (!el) return;
      el.focus();
      const sel = window.getSelection();
      let range: Range;
      if (sel && sel.rangeCount && el.contains(sel.anchorNode)) {
        range = sel.getRangeAt(0);
        // If selection is outside our editor, fall back to end.
        if (!el.contains(range.startContainer)) {
          range = endRange(el);
        }
      } else {
        range = endRange(el);
      }
      range.deleteContents();

      // Smart spacing: if the char before the cursor is non-whitespace and not a newline,
      // insert a space so the chip doesn't fuse with the previous word.
      const needsLeadSpace = (() => {
        const r = range.cloneRange();
        r.collapse(true);
        const before = textBefore(el, r);
        if (!before) return false;
        const ch = before.charAt(before.length - 1);
        return ch !== '' && ch !== ' ' && ch !== '\n' && ch !== ' ';
      })();
      if (needsLeadSpace) {
        const space = document.createTextNode(' ');
        range.insertNode(space);
        range.setStartAfter(space);
      }

      const chip = makeChip(name);
      range.insertNode(chip);

      // Move caret AFTER chip
      const after = document.createRange();
      after.setStartAfter(chip);
      after.collapse(true);
      sel?.removeAllRanges();
      sel?.addRange(after);

      fireChange();
    }, [fireChange]);

    /** Insert the next free parameter_N. Returns the chosen name. */
    const insertNextParameter = useCallback((): string => {
      const text = serialize();
      const used = new Set<string>();
      text.replace(VAR_RE, (_m, n: string) => { used.add(n); return _m; });
      // Also exclude names used elsewhere in the same template so chips stay
      // unique across header / body / footer / buttons.
      for (const n of externalUsedNames?.() ?? []) used.add(n);
      let n = 1;
      while (used.has(`parameter_${n}`)) n++;
      const name = `parameter_${n}`;
      insertVariable(name);
      return name;
    }, [serialize, insertVariable, externalUsedNames]);

    useImperativeHandle(ref, () => ({
      insertVariable,
      insertNextParameter,
      focus: () => editorRef.current?.focus(),
    }), [insertVariable, insertNextParameter]);

    /** Block typing past maxLength. Don't let users break a chip with key edits. */
    const onBeforeInput = (e: React.FormEvent<HTMLDivElement>) => {
      const evt = e.nativeEvent as InputEvent;
      const current = serialize();
      // Always ensure typing inside a chip is disallowed.
      const sel = window.getSelection();
      if (sel && sel.anchorNode) {
        const insideChip = (sel.anchorNode.parentElement as HTMLElement | null)?.closest('[data-var]');
        if (insideChip) {
          e.preventDefault();
          // Move caret to *after* the chip, then redo the input.
          const r = document.createRange();
          r.setStartAfter(insideChip);
          r.collapse(true);
          sel.removeAllRanges();
          sel.addRange(r);
          if (evt.data) document.execCommand('insertText', false, evt.data);
          return;
        }
      }
      // Length cap (counts including chip serialized form, which is approximate).
      if (evt.inputType === 'insertText' && current.length + (evt.data?.length ?? 1) > maxLength) {
        e.preventDefault();
      }
    };

    /** Handle backspace/delete at chip boundary: remove the whole chip. */
    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) return;
      const range = sel.getRangeAt(0);
      if (!range.collapsed) return; // a regular range delete handles itself

      if (e.key === 'Backspace') {
        const node = range.startContainer;
        const offset = range.startOffset;
        // If at start of a text node, look at the previous sibling.
        let prev: Node | null = null;
        if (node.nodeType === Node.TEXT_NODE && offset === 0) {
          prev = (node as Text).previousSibling;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          prev = (node as Element).childNodes[offset - 1] ?? null;
        }
        if (prev && prev instanceof HTMLElement && prev.dataset.var) {
          e.preventDefault();
          prev.remove();
          fireChange();
        }
      } else if (e.key === 'Delete') {
        const node = range.startContainer;
        const offset = range.startOffset;
        let next: Node | null = null;
        if (node.nodeType === Node.TEXT_NODE && offset === (node.textContent?.length ?? 0)) {
          next = (node as Text).nextSibling;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          next = (node as Element).childNodes[offset] ?? null;
        }
        if (next && next instanceof HTMLElement && next.dataset.var) {
          e.preventDefault();
          next.remove();
          fireChange();
        }
      }
    };

    /** Strip rich content from pastes. */
    const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      if (!text) return;
      document.execCommand('insertText', false, text.slice(0, Math.max(0, maxLength - serialize().length)));
    };

    return (
      <div className="relative">
        <div
          ref={editorRef}
          role="textbox"
          aria-multiline="true"
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={fireChange}
          onBeforeInput={onBeforeInput}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          spellCheck
          className={clsx(
            'w-full rounded-lg border bg-surface px-3.5 py-3 text-sm text-text-primary leading-relaxed',
            'transition-colors duration-150',
            'hover:border-border-strong',
            'focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft',
            'border-border whitespace-pre-wrap break-words',
            'empty:before:content-[attr(data-placeholder)] empty:before:text-text-muted',
            disabled && 'opacity-60 cursor-not-allowed',
          )}
          style={{ minHeight: `${rows * 1.6}rem` }}
          data-placeholder={placeholder ?? ''}
        />
      </div>
    );
  },
);
RichBodyEditor.displayName = 'RichBodyEditor';

export const VariableInsertButton: React.FC<{ onClick: () => void; label?: string; disabled?: boolean }> = ({
  onClick, label = 'Add parameter', disabled,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-semibold text-text-primary transition hover:border-primary hover:bg-primary-soft hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
  >
    <Plus size={13} /> {label}
  </button>
);

/* ---------- helpers ---------- */

function makeChip(name: string): HTMLSpanElement {
  const span = document.createElement('span');
  span.contentEditable = 'false';
  span.dataset.var = name;
  span.textContent = `{{${name}}}`;
  span.className =
    'inline align-baseline px-1 mx-0.5 rounded ' +
    'bg-primary-soft text-primary font-mono text-[0.85em] font-medium ' +
    'select-all cursor-pointer';
  span.title = `Variable: ${name}`;
  return span;
}

function endRange(el: HTMLElement): Range {
  const r = document.createRange();
  r.selectNodeContents(el);
  r.collapse(false);
  return r;
}

function textBefore(root: HTMLElement, range: Range): string {
  // Best-effort: grab the textContent up to range start by walking nodes
  // until we hit the start container.
  let collected = '';
  let stop = false;
  const walk = (node: Node) => {
    if (stop) return;
    if (node === range.startContainer) {
      if (node.nodeType === Node.TEXT_NODE) {
        collected += (node.textContent ?? '').slice(0, range.startOffset);
      }
      stop = true;
      return;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      collected += node.textContent ?? '';
    } else if (node instanceof HTMLElement && node.dataset.var) {
      collected += `{{${node.dataset.var}}}`;
    } else {
      node.childNodes.forEach(walk);
    }
  };
  root.childNodes.forEach(walk);
  return collected;
}

/* ---------- formatting helpers (exported for footer/header) ---------- */

export const FormattingButton: React.FC<{
  icon: 'bold' | 'italic' | 'strike';
  onClick: () => void;
  active?: boolean;
}> = ({ icon, onClick, active }) => {
  const Icon = icon === 'bold' ? Bold : icon === 'italic' ? Italic : Strikethrough;
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'inline-flex h-7 w-7 items-center justify-center rounded-md transition',
        active
          ? 'bg-primary-soft text-primary'
          : 'text-text-muted hover:bg-surface-alt hover:text-text-primary',
      )}
      tabIndex={-1}
    >
      <Icon size={13} />
    </button>
  );
};
